import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, Subscription, combineLatest, map, shareReplay, take, tap } from 'rxjs';

import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';

import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { CurationAgentModel } from '../../models/curation-agent.model';
import { CurationAgentSourceModel } from '../../models/curation-agent-source.model';
import { AgentRouteSectionEnum, agentSectionRoute } from '../../AutomationAgentsRouter/automation-agents-route.const';
import { AgentUsageDialogComponent } from '../AgentUsageDialog/agent-usage-dialog.component';
import { AgentUsagePeriodEnum } from '../AgentUsageDashboard/agent-usage-dashboard.component';
import { AgentUsageService } from '../../services/agent-usage.service';
import { CurationAgentsService } from '../../services/curation-agents.service';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { AutomationAgentsHypeService } from '../../Core/services/automation-agents-hype.service';
import { CurationAgentPanelComponent } from '../CurationAgentPanel/curation-agent-panel.component';

/** What the agent list is ordered by. The direction is held separately, as SortOrderEnum */
export enum AgentSortFieldEnum {
    name = 'name',
    created = 'created',
    edited = 'edited'
}

@Component({
    selector: 'agpr-agent-management',
    templateUrl: './agent-management.component.html',
    styleUrls: ['./agent-management.component.scss'],
    standalone: false
})
export class AgentManagementComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;

    readonly sortFields: AgentSortFieldEnum[] = [
        AgentSortFieldEnum.name,
        AgentSortFieldEnum.created,
        AgentSortFieldEnum.edited
    ];

    /** The field names the sort, the direction toggle beside it says which way it runs */
    readonly sortLabels: Record<AgentSortFieldEnum, string> = {
        [AgentSortFieldEnum.name]: 'AutomationAgents.list.sort.name',
        [AgentSortFieldEnum.created]: 'AutomationAgents.list.sort.created',
        [AgentSortFieldEnum.edited]: 'AutomationAgents.list.sort.edited'
    };

    readonly ascendingIcon: string = 'fa-regular fa-arrow-down-short-wide';
    readonly descendingIcon: string = 'fa-regular fa-arrow-up-wide-short';

    sortField: AgentSortFieldEnum = AgentSortFieldEnum.name;
    sortOrder: SortOrderEnum = SortOrderEnum.ascending;
    sortMenuVisible: boolean = false;
    /** The window the usage panel is on, so expanding it into the dialog keeps it */
    usagePeriod: AgentUsagePeriodEnum = AgentUsagePeriodEnum.current;

    agents$: Observable<CurationAgentModel[]>;
    readonly quotaExceeded$: Observable<boolean> = this.curationAgentsService.quotaExceeded$;

    /** Cards stay open independently, so any number of them can be expanded at once */
    private readonly expandedAgentIds: Set<number> = new Set<number>();
    /**
     * Agents whose actions have been fetched. Tracked explicitly rather than inferred from the model:
     * the list response already carries sources, and an agent may legitimately have no actions, so
     * there is nothing on the agent itself that distinguishes "not loaded" from "nothing to show".
     */
    private readonly detailsLoadedAgentIds: Set<number> = new Set<number>();
    /** Re-emits the list when the sort changes, without going back to the API for it */
    private readonly sortChanged: BehaviorSubject<void> = new BehaviorSubject<void>(null);
    private readonly subscriptions: Subscription[] = [];

    constructor(
        private curationAgentsService: CurationAgentsService,
        private agentUsageService: AgentUsageService,
        private coreDialogService: CoreDialogService,
        private localizationService: LocalizationService,
        private elementRef: ElementRef,
        private hypeService: AutomationAgentsHypeService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.agents$ = combineLatest([this.curationAgentsService.agents$, this.sortChanged]).pipe(
            // A refreshed list is rebuilt into new models, so anything fetched before is gone with it
            tap(() => this.detailsLoadedAgentIds.clear()),
            map(([agents]) => this.sorted(agents)),
            // Saving an agent refreshes the list, and the list carries no actions - so any card
            // still open has to be filled back in rather than sitting there empty
            tap(agents => agents
                .filter(agent => this.expandedAgentIds.has(agent.id))
                .forEach(agent => this.loadDetails(agent))),
            shareReplay(1)
        );

        // The empty state is the shared hype page, whose call to action routes back here
        this.subscriptions.push(
            this.hypeService.newAgentRequested$.subscribe(() => this.createAgent())
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    get sortOrderIcon(): string {
        return this.sortOrder === SortOrderEnum.ascending ? this.ascendingIcon : this.descendingIcon;
    }

    /** The left half of the control just flips the direction, keeping the field as it is */
    toggleSortOrder(): void {
        this.sortOrder = this.sortOrder === SortOrderEnum.ascending
            ? SortOrderEnum.descending
            : SortOrderEnum.ascending;
        this.sortChanged.next();
    }

    /** The caret opens the list of fields to sort by */
    toggleSortMenu(): void {
        this.sortMenuVisible = !this.sortMenuVisible;
    }

    closeSortMenu(): void {
        this.sortMenuVisible = false;
    }

    selectSortField(field: AgentSortFieldEnum): void {
        this.sortField = field;
        this.closeSortMenu();
        this.sortChanged.next();
    }

    isExpanded(agent: CurationAgentModel): boolean {
        return this.expandedAgentIds.has(agent.id) && this.detailsLoadedAgentIds.has(agent.id);
    }

    /** Expanding a card loads its sources and actions, which the list endpoint does not return */
    toggleExpanded(agent: CurationAgentModel, expanded: boolean): void {
        if (expanded) {
            this.expandedAgentIds.add(agent.id);
        } else {
            this.expandedAgentIds.delete(agent.id);
        }

        if (expanded) {
            this.loadDetails(agent);
        }
    }

    /** The sources and actions the list endpoint leaves out, fetched once per listed agent */
    private loadDetails(agent: CurationAgentModel): void {
        if (this.detailsLoadedAgentIds.has(agent.id)) {
            return;
        }

        this.subscriptions.push(
            this.curationAgentsService.get(agent.id).pipe(take(1)).subscribe({
                next: loaded => {
                    agent.sources = loaded.sources;
                    agent.positiveActions = loaded.positiveActions;
                    agent.negativeActions = loaded.negativeActions;
                    this.loadCuratedCounts(agent);
                    this.detailsLoadedAgentIds.add(agent.id);
                }
            })
        );
    }

    /**
     * How much each source has had kept over the last day. There is no facet for it, so it is a
     * count per source - which is why it waits until the card is expanded to ask for them.
     */
    private loadCuratedCounts(agent: CurationAgentModel): void {
        this.subscriptions.push(
            this.agentUsageService
                .totalsBySource(DateQueryModel.getLast24HoursQuery(), agent.id)
                .pipe(take(1))
                .subscribe(totals => agent.sources.forEach(
                    source => source.curatedLast24h = totals.get(source.id)?.curated ?? 0
                ))
        );
    }

    /** The dashboard's arrow slides it out over the page rather than hiding the panel */
    openUsageDialog(): void {
        this.coreDialogService.open(AgentUsageDialogComponent, {
            inputs: {
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                overlayContainer: document.getElementById('main-content') ?? this.elementRef.nativeElement,
                // Opened on whatever window the panel was showing, rather than back at the default
                selectedPeriod: this.usagePeriod
            },
            // And the panel picks up wherever the dialog was left, so closing it keeps the window
            outputs: {
                periodSelected: (period: AgentUsagePeriodEnum) => this.usagePeriod = period
            }
        });
    }

    createAgent(): void {
        this.openPanel();
    }

    editAgent(agent: CurationAgentModel): void {
        // Sources are a separate resource, so the listed agent has to be reloaded in full first
        this.curationAgentsService.get(agent.id).pipe(take(1)).subscribe(loaded => this.openPanel(loaded));
    }

    /** Deleting takes the agent's sources and its filters with it, so it is worth a second look */
    deleteAgent(agent: CurationAgentModel): void {
        const confirmDialog: ConfirmDialogModel = {
            inputs: {
                id: 'agentManagement.confirmDelete',
                heading: 'AutomationAgents.confirmDelete.heading',
                alertType: messageSeverityEnum.warning,
                alertMessage: 'AutomationAgents.confirmDelete.alertMessage',
                message: this.localizationService.transform(
                    'AutomationAgents.confirmDelete.message',
                    { name: agent.name }
                ),
                okLabel: 'AutomationAgents.confirmDelete.ok'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.curationAgentsService.delete(agent).subscribe();
                    }
                }
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, confirmDialog);
    }

    /** Opens the agent's own dashboard, the way viewing a theme does in Social */
    viewAgent(agent: CurationAgentModel): void {
        this.router.navigate([agentSectionRoute(agent.id, AgentRouteSectionEnum.dashboard)]);
    }

    /** Lands on the source's own coverage in a new tab, as the monitoring source picker does */
    viewSource(source: CurationAgentSourceModel): void {
        this.curationAgentsService.viewSource(source);
    }

    toggleSource(agent: CurationAgentModel, source: CurationAgentSourceModel): void {
        this.curationAgentsService.setSourceStatus(agent, source).subscribe();
    }

    removeSource(agent: CurationAgentModel, source: CurationAgentSourceModel): void {
        this.curationAgentsService.removeSource(agent, source).subscribe(() => {
            agent.sources = agent.sources.filter(item => item !== source);
        });
    }

    /** When the agent last changed: its edit date, or its creation date if it has never been edited */
    private editedDate(agent: CurationAgentModel): number {
        return agent.lastModifiedDate ?? agent.createdDate ?? 0;
    }

    private sorted(agents: CurationAgentModel[]): CurationAgentModel[] {
        const direction = this.sortOrder === SortOrderEnum.ascending ? 1 : -1;

        return [...agents].sort((a, b) => this.compare(a, b) * direction);
    }

    private compare(a: CurationAgentModel, b: CurationAgentModel): number {
        switch (this.sortField) {
            case AgentSortFieldEnum.created:
                return (a.createdDate ?? 0) - (b.createdDate ?? 0);
            case AgentSortFieldEnum.edited:
                // An agent that has never been edited has no edit date at all, so it sorts by when
                // it was created - otherwise every untouched agent piles up at one end of the list
                return this.editedDate(a) - this.editedDate(b);
            case AgentSortFieldEnum.name:
            default:
                return a.name.localeCompare(b.name);
        }
    }

    private openPanel(agent?: CurationAgentModel): void {
        this.coreDialogService.open(CurationAgentPanelComponent, {
            inputs: {
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                overlayContainer: document.getElementById('main-content') ?? this.elementRef.nativeElement,
                agent: agent
            }
        });
    }
}
